/**
 * Minimal i18n for the presentation site (PRD §16.1 — one shared project,
 * multilingual in a few languages): **English · Spanish · French**.
 *
 * "Modern approach" per the 2026-10-02 ruling: the language follows the end
 * user's **browser preference / regional settings** (`navigator.languages`,
 * base-tag match: e.g. `es-MX` → `es`) with **fallback to English** — no on-page
 * switcher is shown. `document.documentElement.lang` is kept in sync.
 *
 * Server-provided content (agency name, public description, screening questions,
 * document names) is NOT translated — it is the agency's own content (PRD §11.2).
 */

import { useEffect, useState } from "react";

export const SUPPORTED_LOCALES = ["en", "es", "fr"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

const en = {
  // header
  "header.staffing": "Temporary staffing",
  "header.alreadyRegistered": "Already registered? Check my status →",
  // the compact (phone) header splits the link: the question (regular) + the action (bold)
  "header.alreadyRegisteredQ": "Already registered?",
  "header.checkMyStatus": "Check my status →",
  "header.verifyMe": "Verify me",
  "header.home": "Home",
  "done.backToHome": "Back to home",

  // landing
  "landing.applyingTo": "Select the agency you want to apply to",
  "landing.readMore": "Read more",
  "landing.showLess": "Show less",
  // the neutral Register / Log in landing (T-SA-19.01, PRD §19.1)
  "landing.heroTitle": "Flexible opportunities start here.",
  "landing.heroSub": "Join our talent community, connect with our clients, and take the next step in your career.",
  "landing.trustFlexible": "Flexible jobs",
  "landing.trustCareer": "Career opportunities",
  "landing.trustProfile": "One candidate profile",
  "landing.register": "Register here",
  "landing.alreadyRegistered": "Already registered? Log in here",
  // "I agree to the Terms of Use and Privacy Policy" (the two documents are links)
  "landing.agreePrefix": "I agree to the",
  "landing.termsLink": "Terms of Use",
  "landing.agreeAnd": "and",
  "landing.privacyLink": "Privacy Policy",
  "landing.privacyPending": "The privacy policy will be published soon.",
  "landing.termsFooter": "Terms of use",
  "landing.termsPending": "The terms of use will be published soon.",
  "landing.jobsSelected": "{count} selected",
  "landing.selectAgencyFirst": "Select an agency",
  "landing.passwordHelpTitle": "No password yet?",
  "landing.passwordHelpBody":
    "You log in with a password you set yourself: when you confirmed your e-mail, we sent you a link to set it. Didn't get it, or did it expire? Request a new one here.",
  "header.emailConfirmedShort": "E-mail confirmed",
  "header.emailConfirmedLoginHint": "You can log in now.",
  "header.verifyWhatsapp": "Verify WhatsApp",

  // the registration step bar (T-SA-19.01 design)
  "steps.jobs": "Job types",
  "steps.regions": "Regions",
  "steps.terms": "Terms",
  "steps.about": "About you",
  "steps.verify": "Confirm contact",
  "steps.caption": "Step {n} of {total} · {label}",
  "steps.allDone": "All steps completed",
  "steps.progress": "Registration progress",
  "steps.backTo": "Back to {label}",

  // wizard common
  "wizard.cancel": "Cancel",
  "wizard.loading": "Loading…",
  "wizard.back": "Back",
  "wizard.continue": "Continue",
  "wizard.errorTitle": "We could not load the application form",

  // jobs step
  "jobs.title": "What kind of work interests you?",
  "jobs.subtitle": "Pick everything you're happy to do. You can change this later.",
  "jobs.empty": "No job types are offered by this agency yet.",
  "jobs.err": "Pick at least one job type.",

  // country step
  // regions step
  "regions.title": "Which regions can you work in?",
  "regions.subtitle": "Shifts last a few hours, so pick places you can reach easily.",
  "regions.err": "Pick at least one area.",

  // terms step (T-SA-23.05)
  "terms.title": "Terms of use",
  "terms.subtitle": "Please read and accept the terms before you add your personal details.",
  "terms.err": "Please agree to the Terms of Use and Privacy Policy to continue.",

  // about step
  "about.title": "A few details about you",
  "about.subtitle": "This is all we need to get you reviewed — no documents required yet.",
  "about.fullName": "Full name",
  "about.birthDate": "Date of birth",
  "about.gender": "Gender",
  "about.gender.pick": "Select…",
  "about.gender.male": "Male",
  "about.gender.female": "Female",
  "about.gender.preferNotToDisclose": "I prefer not to disclose",
  "about.taxId": "Tax ID",
  "about.whatsapp": "WhatsApp number",
  "about.email": "Email",
  "about.consent":
    "I agree to be contacted on WhatsApp about shift invitations, reminders and confirmations. We will not share your number with anyone else.",
  "about.consentTelegram": "I understand I will have to enroll via Telegram — I will have to provide my phone number there (the Telegram share-phone button), following the instructions in the next step.",
  "about.docsTitle": "Optional documents",
  "about.docsSubtitle":
    "{agency} requests these documents — attaching them now is never blocking, you can also add them later.",
  "about.attach": "Attach document (PDF or image)",
  "about.attachType": "Attach: {type} (PDF or image)",
  "about.remove": "Remove",
  "about.submit": "Submit application",
  "about.submitting": "Submit application…",
  "question.yes": "Yes",
  "question.no": "No",

  // submit validation
  "submit.err.selectAgency": "Select the agency you are applying to before submitting.",
  "submit.err.name": "Add your full name.",
  "submit.err.dob": "Add your date of birth.",
  "submit.err.dobAge": "You must be at least 16 years old to register.",
  "submit.err.dobInvalid": "Check your date of birth.",
  "submit.err.gender": "Select your gender.",
  "submit.err.taxId": "Add your tax ID.",
  "submit.err.whatsapp": "Add a valid WhatsApp number.",
  "submit.err.consent":
    "Please agree to be contacted on WhatsApp — we will not share your number.",
  "about.chatbot": "How do you want to be contacted?",
  "about.chatbotWhatsapp": "WhatsApp",
  "about.chatbotTelegram": "Telegram",
  "about.telegramNote":
    "You are choosing Telegram. On the next step you will link your Telegram chat with one tap, and then you will have to provide your phone number in Telegram (the share-phone button) - we send your notifications there instead of WhatsApp. The registration completes only after the phone number is confirmed.",
  "submit.err.email": "Add a valid email address.",
  "submit.err.flName": "Add your first and last name.",
  "submit.err.questions": "Answer every screening question (or attach its document).",
  "submit.err.failed": "Submission failed. Please try again.",
  "submit.err.nameLength": "Add your full name.",

  // verification
  "verify.title": "Confirm your WhatsApp number",
  // Amendment 1.3 (chatbot PRD §15): the CANDIDATE initiates — the wa.me link card, no code typed.
  "verify.body":
    "Tap the button below — WhatsApp opens with a message already prefilled for {whatsapp}. Just press SEND: this confirms your number as yours (no code to type).",
    "verify.resend": "I did not get the message — new link",
  "verify.toast.registered": "Registered. Proceed with confirmations.",
  "verify.toast.registeredUploadsFailed":
    "Registered, but {count} document(s) could not be uploaded. You can add them later from your profile.",
    "verify.toast.resent": "Link re-sent — open WhatsApp and press SEND.",
    "verify.err.link": "Could not start the WhatsApp confirmation. Please try again in a moment.",
  "verify.whatsapp.openLink": "Open WhatsApp and press SEND",
  "verify.whatsapp.preparing": "Preparing the link…",
  "verify.whatsapp.waiting": "Waiting for you to press SEND… (this page updates automatically)",
  "verify.whatsapp.qrCaption": "Registering on a computer? Scan this code with your phone, then press SEND in WhatsApp.",
  "verify.whatsapp.done": "WhatsApp confirmed — all set!",
  "verify.err.confirm": "Confirmation failed. Please try again.",
  "verify.err.send": "Could not send the WhatsApp code. Please try again in a moment.",
  // Telegram primary (chatbot PRD §5.2/§7)
  "verify.telegram.title": "Link your Telegram",
  "verify.telegram.body": "Press the button below and then START in the Telegram chat — this links your chat so we can send you invitations, assignments and clocking prompts here.",
  "verify.telegram.openLink": "Open Telegram and press START",
  "verify.telegram.preparing": "Preparing the link…",
  "verify.telegram.waiting": "Waiting for you to press START… (this page updates automatically)",
  "verify.telegram.done": "Telegram linked ✓ — all set!",
  "verify.telegram.skip": "Continue without linking (you can link later via 'Verify me')",
  "verify.telegram.toast.linked": "Telegram linked — we will reach you here.",
  "verify.telegram.err.botConfig": "Telegram is not configured on this site — please pick WhatsApp instead.",
  "verify.switchToWhatsapp": "I'd rather confirm via WhatsApp →",
  "verify.switchToTelegram": "I'd rather confirm via Telegram →",
  "verify.waitingNote": "The confirmation may take a few minutes — please wait. When the confirmation message arrives as a reply in your messenger, it is safe to proceed.",
  "verify.telegram.qrCaption": "On the phone? Scan this code to open the registration chat in Telegram, then press START.",
  "verify.telegram.linkCode.title": "No START prompt in the chat?",
  "verify.telegram.linkCode.body": "Telegram only pre-fills START for a brand-new chat. If your chat already existed, open the bot and send this command:",
  "verify.telegram.phoneTitle": "Confirm your phone number",
  "verify.telegram.phoneBody": "You need to provide the phone number in order to receive job information. Press START (or the button on this page) and then the SHARE-PHONE button in the chat - the registration completes only after that.",
  "verify.telegram.phoneBtn": "Confirm phone number by Telegram",
  "verify.telegram.phoneToast": "Share-phone message sent — complete the confirmation in Telegram.",
  "email.title": "✉️ Verify your e-mail — necessary to log into your cabinet",
  "email.titleConfirmed": "Your e-mail is already confirmed ✓",
  "email.verified": "Your e-mail is verified. Thank you!",
  "email.sent": "Code sent to {email}.",
  "email.intro":
    "Optional: verify {email} to keep e-mail as a backup contact channel. You can also do this later from your profile.",
  "email.send": "Send code to my e-mail",
  "email.verify": "Verify e-mail",
  "email.resend": "Resend",
  "email.skip": "Skip for now →",
  "email.toast.sent": "We sent a code to your e-mail — enter it below to verify (optional).",
  "email.toast.verified": "E-mail verified",
  "email.err.code": "Enter the 6-digit code from your e-mail.",
  "email.err.send": "Could not send the e-mail code. Please try again in a moment.",
  "email.err.confirm": "E-mail confirmation failed. Please try again.",
  "email.confirmTitle": "Confirm your e-mail address",
  "email.focusNote": "One step to finish: confirm your e-mail — the platform creates your account and sends you the password-setup link.",
  "email.whatsappPending": "Your WhatsApp number isn't confirmed yet — you won't be invited to shifts until it is.",
  "email.confirmWhatsapp": "Confirm WhatsApp",
  "email.recommended": "Recommended — your account is created and the password-setup link is sent to this e-mail as soon as the code is confirmed.",
  "header.forgotToast": "Your saved registration data was cleared from this browser.",
  "header.forgetMyData": "Forget my data",
  "header.verifyMeWithName": "Verify me — {name}",
  "header.registerToLogin": "Register and confirm your account to log in.",
  "header.loginTooltip": "Login if you confirmed your e-mail.",
  "header.confirmEmailToLogin": "Confirm your e-mail to log in.",
  "header.confirmEmailActionHint": "Click 'Verify me' to confirm your e-mail.",
  "header.emailConfirmedTitle": "Your e-mail is confirmed.",
  "header.emailConfirmedHint": "Use \"Already registered? Check my status\" to log in.",
  "header.staleRegistration": "Your saved application is no longer on record — please register again.",
  "header.accountMenu": "Account menu",
  "header.registeredWith": "Registered with {agency}",
  "header.forgetConfirmTitle": "Forget your saved registration on this device?",
  "header.forgetConfirmBody": "Only this browser's copy is removed — your application with the agency is not affected.",
  "header.forgetConfirm": "Forget",
  "header.cancel": "Cancel",
  "email.alreadyConfirmed": "Your e-mail is already confirmed ✓",
  "email.passwordSent": "The password-setup link for {site} was sent to {email}. Open it to set your password. If the browser shows a security warning on that site, accepting it will let you proceed.",
  "email.password.resend": "Resend password link",
  "email.password.toast.resent": "A new password link was sent to {email}.",
  "email.password.notSent.noSite": "Your e-mail is confirmed, but the password link could not be sent: Please contact the agency, then use \"Resend password link\".",
  "email.password.notSent.failed": "Your e-mail is confirmed, but the password link could not be sent. Use \"Resend password link\", or \"Forgot password\" on {site}.",
  "email.password.unknown": "Didn't receive the password link, or did it expire? Use \"Resend password link\", or \"Forgot password\" on {site}.",
  "email.password.err.resend": "Could not resend the password link. Please try again.",
  "email.password.err.notEligible": "No confirmed account matches this e-mail. Confirm your e-mail first.",
  "email.password.err.tooMany": "A password link was just sent. Please wait a moment and try again.",
  "email.password.err.passwordSet": "Your password is already set. Use \"Forgot password\" on the sign-in page to change it.",
  "header.passwordLink": "Didn't get the password link?",
  "email.password.signInPage": "the sign-in page",
  "email.continueToStatus": "Continue →",
  "email.err.first": "Add a valid email address first.",

  // done step
  "done.thanks": "Thanks, {name}! Your profile is under review.",
  "done.thanksNoName": "Thanks! Your profile is under review.",
  "done.body":
    "{agency} will check your details and reach out on {messenger} once you're approved. You can already sign in to track your status on the full portal.",
  "done.messengerAny": "your linked messenger",
  "done.cta": "Check my status",

  // toasts / errors
  "toast.selected": "Selected: {name}",
  "toast.whatsappConfirmed": "WhatsApp confirmed",
  "toast.questionsUnavailable":
    "Could not load the registration questions — continuing without them.",
  "toast.dictLoadFailed": "Could not load the application form.",
  "err.duplicate":
    "This email or ID is already registered with {agency}. Use \"Already registered? Check my status\" above to sign in.",
  "err.http": "Request failed (HTTP {status})",
  "err.registerFailed": "Registration failed. Please try again.",
  "err.uploadFailed": "Uploading \"{name}\" failed (HTTP {status}).",
  "err.uploadFailedGeneric": "Uploading the document failed.",
    "err.codeIncorrect.email": "Incorrect code. Check your inbox and try again.",
  "err.retry": "Try again",
  "demo.emailCode": "[demo] E-mail verification code: {code}",
  "lang.tip": "Use the language switcher to switch to {language}",
  "demo.agencyName": "Demo agency",
  "header.noAgency": "No agency available",
  "landing.noAgency": "No agency available at the moment — temporary jobs are not being advertised right now. Please try again later.",
  "landing.genericBlurb": "We have temporary jobs in events, hospitality, warehouses and more — register once and get invited to the shifts that match you, right on WhatsApp.",
  "landing.preselectBlurb": "Select the staffing agency you want to apply to — its offer appears here.",
  "questions.docFallback": "registration document",
} as const;

export type TKey = keyof typeof en;

const es: Record<TKey, string> = {
  "header.staffing": "Trabajo temporal",
  "header.alreadyRegistered": "¿Ya estás registrado? Consulta mi estado →",
  "header.alreadyRegisteredQ": "¿Ya estás registrado?",
  "header.checkMyStatus": "Consulta mi estado →",
  "header.verifyMe": "Verificarme",
  "header.home": "Inicio",
  "done.backToHome": "Volver al inicio",

  "landing.applyingTo": "Selecciona la agencia a la que quieres postularte",
  "landing.readMore": "Leer más",
  "landing.showLess": "Mostrar menos",
  "landing.heroTitle": "Las oportunidades flexibles empiezan aquí.",
  "landing.heroSub": "Únete a nuestra comunidad de talento, conecta con nuestros clientes y da el siguiente paso en tu carrera.",
  "landing.trustFlexible": "Trabajos flexibles",
  "landing.trustCareer": "Oportunidades profesionales",
  "landing.trustProfile": "Un único perfil de candidato",
  "landing.register": "Regístrate aquí",
  "landing.alreadyRegistered": "¿Ya estás registrado? Inicia sesión aquí",
  "landing.agreePrefix": "Acepto las",
  "landing.termsLink": "Condiciones de uso",
  "landing.agreeAnd": "y la",
  "landing.privacyLink": "Política de privacidad",
  "landing.privacyPending": "La política de privacidad se publicará pronto.",
  "landing.termsFooter": "Condiciones de uso",
  "landing.termsPending": "Las condiciones de uso se publicarán pronto.",
  "landing.jobsSelected": "{count} seleccionados",
  "landing.selectAgencyFirst": "Selecciona una agencia",
  "landing.passwordHelpTitle": "¿Aún no tienes contraseña?",
  "landing.passwordHelpBody":
    "Inicias sesión con una contraseña que estableces tú: al confirmar tu correo te enviamos un enlace para crearla. ¿No lo recibiste o caducó? Pide uno nuevo aquí.",
  "header.emailConfirmedShort": "Correo confirmado",
  "header.emailConfirmedLoginHint": "Ya puedes iniciar sesión.",
  "header.verifyWhatsapp": "Verificar WhatsApp",

  "steps.jobs": "Tipos de trabajo",
  "steps.regions": "Zonas",
  "steps.terms": "Condiciones",
  "steps.about": "Sobre ti",
  "steps.verify": "Confirmar contacto",
  "steps.caption": "Paso {n} de {total} · {label}",
  "steps.allDone": "Todos los pasos completados",
  "steps.progress": "Progreso del registro",
  "steps.backTo": "Volver a {label}",

  "wizard.cancel": "Cancelar",
  "wizard.loading": "Cargando…",
  "wizard.back": "Atrás",
  "wizard.continue": "Continuar",
  "wizard.errorTitle": "No pudimos cargar el formulario de solicitud",

  "jobs.title": "¿Qué tipo de trabajo te interesa?",
  "jobs.subtitle": "Marca todo lo que te apetezca hacer. Podrás cambiarlo más adelante.",
  "jobs.empty": "Esta agencia aún no ofrece tipos de trabajo.",
  "jobs.err": "Elige al menos un tipo de trabajo.",

  "regions.title": "¿En qué regiones puedes trabajar?",
  "regions.subtitle": "Los turnos duran unas horas, así que elige sitios a los que llegues fácilmente.",
  "regions.err": "Elige al menos una zona.",

  // terms step (T-SA-23.05)
  "terms.title": "Condiciones de uso",
  "terms.subtitle": "Lee y acepta las condiciones antes de añadir tus datos personales.",
  "terms.err": "Acepta las Condiciones de uso y la Política de privacidad para continuar.",

  "about.title": "Algunos detalles sobre ti",
  "about.subtitle": "Esto es todo lo que necesitamos para revisar tu solicitud — aún no hacen falta documentos.",
  "about.fullName": "Nombre completo",
  "about.birthDate": "Fecha de nacimiento",
  "about.gender": "Género",
  "about.gender.pick": "Selecciona…",
  "about.gender.male": "Hombre",
  "about.gender.female": "Mujer",
  "about.gender.preferNotToDisclose": "Prefiero no indicarlo",
  "about.taxId": "NIF",
  "about.whatsapp": "Número de WhatsApp",
  "about.email": "Correo electrónico",
  "about.consent":
    "Acepto que me contacten por WhatsApp sobre invitaciones, recordatorios y confirmaciones de turnos. No compartiremos tu número con nadie.",
  "about.consentTelegram": "Entiendo que tendré que registrarme por Telegram — tendré que dar allí mi número de teléfono (el botón de compartir número de Telegram), siguiendo las instrucciones del siguiente paso.",
  "about.docsTitle": "Documentos opcionales",
  "about.docsSubtitle":
    "{agency} solicita estos documentos — adjuntarlos ahora nunca es bloqueante, también puedes añadirlos más adelante.",
  "about.attach": "Adjuntar documento (PDF o imagen)",
  "about.attachType": "Adjuntar: {type} (PDF o imagen)",
  "about.remove": "Quitar",
  "about.submit": "Enviar la solicitud",
  "about.submitting": "Enviando la solicitud…",
  "question.yes": "Sí",
  "question.no": "No",

  "submit.err.selectAgency": "Selecciona la agencia a la que te postulas antes de enviar.",
  "submit.err.name": "Añade tu nombre completo.",
  "submit.err.dob": "Añade tu fecha de nacimiento.",
  "submit.err.dobAge": "Debes tener al menos 16 años para registrarte.",
  "submit.err.dobInvalid": "Revisa tu fecha de nacimiento.",
  "submit.err.gender": "Selecciona tu género.",
  "submit.err.taxId": "Añade tu NIF.",
  "submit.err.whatsapp": "Añade un número de WhatsApp válido.",
  "submit.err.consent":
    "Acepta que te contactemos por WhatsApp — no compartiremos tu número con nadie.",
  "submit.err.email": "Añade una dirección de correo válida.",
  "submit.err.flName": "Añade nombre y apellidos.",
  "submit.err.questions": "Responde a todas las preguntas (o adjunta su documento).",
  "submit.err.failed": "Error al enviar la solicitud. Inténtalo de nuevo.",
  "submit.err.nameLength": "Añade tu nombre completo.",

    "about.chatbot": "¿Cómo quieres que te contactemos?",
  "about.chatbotWhatsapp": "WhatsApp",
  "about.chatbotTelegram": "Telegram",
  "about.telegramNote":
    "Elegiste Telegram. En el siguiente paso enlazarás tu chat de Telegram con un toque y después tendrás que dar tu número de teléfono en Telegram (el botón de compartir número): allí recibirás las notificaciones en lugar de WhatsApp. El registro solo se completa tras confirmar el número de teléfono.",
"verify.title": "Confirma tu número de WhatsApp",
"verify.body": "Pulsa el botón de abajo: WhatsApp se abre con un mensaje ya redactado para {whatsapp}. Solo tienes que pulsar ENVIAR: así confirmamos que el número es tuyo (sin código que teclear).",
"verify.resend": "No me llegó el mensaje - nuevo enlace",
"verify.toast.resent": "Enlace reenviado: abre WhatsApp y pulsa ENVIAR.",
"verify.err.link": "No se pudo iniciar la confirmación por WhatsApp. Inténtalo de nuevo en un momento.",
"verify.whatsapp.openLink": "Abrir WhatsApp y pulsar ENVIAR",
"verify.whatsapp.preparing": "Preparando el enlace…",
"verify.whatsapp.waiting": "Esperando a que pulses ENVIAR… (esta página se actualiza sola)",
"verify.whatsapp.qrCaption": "¿Registrándote en un ordenador? Escanea este código con tu teléfono y pulsa ENVIAR en WhatsApp.",
"verify.whatsapp.done": "WhatsApp confirmado: todo listo!",
"verify.telegram.title": "Vincula tu Telegram",
  "verify.telegram.body": "Pulsa el botón y luego START en el chat de Telegram: así vinculamos tu chat y te enviaremos invitaciones, confirmaciones y fichajes allí.",
  "verify.telegram.openLink": "Abrir Telegram y pulsar START",
  "verify.telegram.preparing": "Preparando el enlace…",
  "verify.telegram.waiting": "Esperando a que pulses START… (esta página se actualiza sola)",
  "verify.telegram.done": "¡Telegram vinculado!",
  "verify.telegram.skip": "Continuar sin vincular (puedes vincular después con 'Verify me')",
  "verify.telegram.toast.linked": "Telegram vinculado: te escribiremos aquí.",
  "verify.telegram.err.botConfig": "Telegram no está configurado en este sitio; elige WhatsApp.",
  "verify.switchToWhatsapp": "Prefiero confirmar por WhatsApp →",
  "verify.switchToTelegram": "Prefiero confirmar por Telegram →",
  "verify.waitingNote": "La confirmación puede tardar unos minutos; espera. Cuando llegue el mensaje de confirmación como respuesta en tu mensajería, puedes continuar sin problema.",
  "verify.telegram.qrCaption": "¿Desde el móvil? Escanea este código para abrir el chat de registro en Telegram y pulsa START.",
  "verify.telegram.linkCode.title": "¿No aparece START en el chat?",
  "verify.telegram.linkCode.body": "Telegram solo prellena START en un chat nuevo. Si tu chat ya existía, abre el bot y envía este comando:",
  "verify.telegram.phoneTitle": "Confirma tu número de teléfono",
  "verify.telegram.phoneBody": "Necesitas dar el número de teléfono para recibir información de trabajo. Pulsa START (o el botón de esta página) y luego el botón de COMPARTIR NÚMERO en el chat: el registro solo se completa después.",
  "verify.telegram.phoneBtn": "Confirmar número de teléfono por Telegram",
  "verify.telegram.phoneToast": "Mensaje enviado — completa la confirmación en Telegram.",
    "verify.toast.registered": "Registro completado. Continúa con las confirmaciones.",
    "verify.toast.registeredUploadsFailed":
      "Registro completado, pero no se han podido subir {count} documento(s). Puedes añadirlos más tarde desde tu perfil.",
      "verify.err.confirm": "Error en la confirmación. Inténtalo de nuevo.",
  "verify.err.send": "No pudimos enviar el código por WhatsApp. Inténtalo en unos instantes.",
  "email.title": "✉️ Verifica tu correo — necesario para entrar a tu cabinet",
  "email.titleConfirmed": "Tu correo ya está confirmado ✓",
  "email.verified": "Tu correo está verificado. ¡Gracias!",
  "email.sent": "Código enviado a {email}.",
  "email.intro":
    "Opcional: verifica {email} para tener el correo como canal de contacto alternativo. También puedes hacerlo más adelante desde tu perfil.",
  "email.send": "Enviar código a mi correo",
  "email.verify": "Verificar correo",
  "email.resend": "Reenviar",
  "email.skip": "Saltar por ahora →",
  "email.toast.sent": "Enviamos un código a tu correo — introdúcelo abajo para verificarlo (opcional).",
  "email.toast.verified": "Correo verificado",
  "email.err.code": "Introduce el código de 6 dígitos de tu correo.",
  "email.err.send": "No pudimos enviar el código a tu correo. Inténtalo en unos instantes.",
  "email.err.confirm": "Error al verificar el correo. Inténtalo de nuevo.",
  "email.confirmTitle": "Confirmar tu dirección de correo",
  "email.focusNote": "Un paso para terminar: confirma tu correo; la plataforma crea tu cuenta y te envía el enlace para establecer la contraseña.",
  "email.whatsappPending": "Tu número de WhatsApp aún no está confirmado: no recibirás invitaciones a turnos hasta que lo esté.",
  "email.confirmWhatsapp": "Confirmar WhatsApp",
  "email.recommended": "Recomendado: tu cuenta se crea y el enlace de contraseña se envía a este correo al confirmar el código.",
  "header.forgotToast": "Los datos de tu registro fueron borrados de este navegador.",
  "header.forgetMyData": "Olvidar mis datos",
  "header.verifyMeWithName": "Verificarme — {name}",
  "header.registerToLogin": "Regístrate y confirma tu cuenta para iniciar sesión.",
  "header.loginTooltip": "Inicia sesión si confirmaste tu correo.",
  "header.confirmEmailToLogin": "Confirma tu correo para iniciar sesión.",
  "header.confirmEmailActionHint": "Pulsa 'Verificarme' para confirmar tu correo.",
  "header.emailConfirmedTitle": "Tu correo está confirmado.",
  "header.emailConfirmedHint": "Usa \"¿Ya estás registrado? Consulta mi estado\" para iniciar sesión.",
  "header.staleRegistration": "Tu solicitud guardada ya no está en el registro; vuelve a registrarte.",
  "header.accountMenu": "Menú de la cuenta",
  "header.registeredWith": "Registrado en {agency}",
  "header.forgetConfirmTitle": "¿Olvidar tu registro guardado en este dispositivo?",
  "header.forgetConfirmBody": "Solo se borra la copia de este navegador; tu solicitud en la agencia no se ve afectada.",
  "header.forgetConfirm": "Olvidar",
  "header.cancel": "Cancelar",
  "email.alreadyConfirmed": "Tu correo ya está confirmado ✓",
  "email.passwordSent": "El enlace para establecer la contraseña de {site} fue enviado a {email}. Ábrelo para definirla. Si el navegador muestra una advertencia de seguridad en ese sitio, acéptala para continuar.",
  "email.password.resend": "Reenviar el enlace de contraseña",
  "email.password.toast.resent": "Enviamos un nuevo enlace de contraseña a {email}.",
  "email.password.notSent.noSite": "Tu correo está confirmado, pero no se pudo enviar el enlace de contraseña: Contacta con la agencia y luego usa \"Reenviar el enlace de contraseña\".",
  "email.password.notSent.failed": "Tu correo está confirmado, pero no se pudo enviar el enlace de contraseña. Usa \"Reenviar el enlace de contraseña\" o \"¿Olvidaste tu contraseña?\" en {site}.",
  "email.password.unknown": "¿No recibiste el enlace de contraseña o ha caducado? Usa \"Reenviar el enlace de contraseña\" o \"¿Olvidaste tu contraseña?\" en {site}.",
  "email.password.err.resend": "No se pudo reenviar el enlace de contraseña. Inténtalo de nuevo.",
  "email.password.err.notEligible": "Ninguna cuenta confirmada coincide con este correo. Confirma primero tu correo.",
  "email.password.err.tooMany": "Se acaba de enviar un enlace de contraseña. Espera un momento e inténtalo de nuevo.",
  "email.password.err.passwordSet": "Tu contraseña ya está establecida. Usa \"¿Olvidaste tu contraseña?\" en la página de inicio de sesión para cambiarla.",
  "header.passwordLink": "¿No recibiste el enlace de contraseña?",
  "email.password.signInPage": "la página de inicio de sesión",
  "email.continueToStatus": "Continuar →",
  "email.err.first": "Añade primero una dirección de correo válida.",

  "done.thanks": "¡Gracias, {name}! Tu perfil está en revisión.",
  "done.thanksNoName": "¡Gracias! Tu perfil está en revisión.",
  "done.body":
    "{agency} revisará tus datos y te contactará por {messenger} cuando seas aprobado. Ya puedes iniciar sesión para consultar tu estado en el portal completo.",
  "done.messengerAny": "tu mensajería vinculada",
  "done.cta": "Consultar mi estado",

  "toast.selected": "Seleccionada: {name}",
  "toast.whatsappConfirmed": "WhatsApp confirmado",
  "toast.questionsUnavailable":
    "No pudimos cargar las preguntas de registro — continuamos sin ellas.",
  "toast.dictLoadFailed": "No pudimos cargar el formulario de solicitud.",
  "err.duplicate":
    "Este correo o DNI ya está registrado en {agency}. Usa \"¿Ya estás registrado? Consulta mi estado\" arriba para iniciar sesión.",
  "err.http": "Error de la petición (HTTP {status})",
  "err.registerFailed": "Error en el registro. Inténtalo de nuevo.",
  "err.uploadFailed": "Error al subir \"{name}\" (HTTP {status}).",
  "err.uploadFailedGeneric": "Error al subir el documento.",
    "err.codeIncorrect.email": "Código incorrecto. Revisa tu bandeja de entrada e inténtalo de nuevo.",
  "err.retry": "Reintentar",
  "demo.emailCode": "[demo] Código de verificación de correo: {code}",
  "lang.tip": "Usa el selector de idioma para cambiar a {language}",
  "demo.agencyName": "Agencia de demostración",
  "header.noAgency": "No hay agencias disponibles",
  "landing.noAgency": "No hay ninguna agencia disponible en este momento — no se están anunciando trabajos temporales ahora mismo. Vuelve a intentarlo más tarde.",
  "landing.genericBlurb": "Tenemos trabajos temporales en eventos, hostelería, almacenes y más — regístrate una vez y recibe invitaciones a los turnos que encajan contigo, directamente por WhatsApp.",
  "landing.preselectBlurb": "Selecciona la agencia a la que quieres solicitar — su oferta aparecerá aquí.",
  "questions.docFallback": "documento de registro",
};

const fr: Record<TKey, string> = {
  "about.chatbot": "Comment souhaitez-vous être contacté·e ?",
  "about.chatbotWhatsapp": "WhatsApp",
  "about.chatbotTelegram": "Telegram",
  "about.telegramNote":
    "Vous avez choisi Telegram. À l'étape suivante, vous lierez votre chat Telegram d'un geste, puis vous devrez fournir votre numéro de téléphone dans Telegram (le bouton de partage) : vous y recevrez les notifications à la place de WhatsApp. L'inscription ne se termine qu'après la confirmation du numéro de téléphone.",
  "header.staffing": "Intérim",
  "header.alreadyRegistered": "Déjà inscrit ? Vérifier mon statut →",
  "header.alreadyRegisteredQ": "Déjà inscrit ?",
  "header.checkMyStatus": "Vérifier mon statut →",
  "header.verifyMe": "Me vérifier",
  "header.home": "Accueil",
  "done.backToHome": "Retour à l'accueil",

  "landing.applyingTo": "Sélectionnez l'agence à laquelle vous voulez postuler",
  "landing.readMore": "Lire la suite",
  "landing.showLess": "Afficher moins",
  "landing.heroTitle": "Les opportunités flexibles commencent ici.",
  "landing.heroSub": "Rejoignez notre communauté de talents, entrez en contact avec nos clients et franchissez la prochaine étape de votre carrière.",
  "landing.trustFlexible": "Emplois flexibles",
  "landing.trustCareer": "Opportunités de carrière",
  "landing.trustProfile": "Un seul profil candidat",
  "landing.register": "Inscrivez-vous ici",
  "landing.alreadyRegistered": "Déjà inscrit ? Connectez-vous ici",
  "landing.agreePrefix": "J'accepte les",
  "landing.termsLink": "Conditions d'utilisation",
  "landing.agreeAnd": "et la",
  "landing.privacyLink": "Politique de confidentialité",
  "landing.privacyPending": "La politique de confidentialité sera bientôt publiée.",
  "landing.termsFooter": "Conditions d'utilisation",
  "landing.termsPending": "Les conditions d'utilisation seront bientôt publiées.",
  "landing.jobsSelected": "{count} sélectionnés",
  "landing.selectAgencyFirst": "Sélectionnez une agence",
  "landing.passwordHelpTitle": "Pas encore de mot de passe ?",
  "landing.passwordHelpBody":
    "Vous vous connectez avec un mot de passe que vous définissez vous-même : à la confirmation de votre e-mail, nous vous avons envoyé un lien pour le créer. Pas reçu, ou expiré ? Demandez-en un nouveau ici.",
  "header.emailConfirmedShort": "E-mail confirmé",
  "header.emailConfirmedLoginHint": "Vous pouvez vous connecter maintenant.",
  "header.verifyWhatsapp": "Vérifier WhatsApp",

  "steps.jobs": "Types de poste",
  "steps.regions": "Zones",
  "steps.terms": "Conditions",
  "steps.about": "À propos de vous",
  "steps.verify": "Confirmer le contact",
  "steps.caption": "Étape {n} sur {total} · {label}",
  "steps.allDone": "Toutes les étapes sont terminées",
  "steps.progress": "Progression de l'inscription",
  "steps.backTo": "Revenir à {label}",

  "wizard.cancel": "Annuler",
  "wizard.loading": "Chargement…",
  "wizard.back": "Retour",
  "wizard.continue": "Continuer",
  "wizard.errorTitle": "Impossible de charger le formulaire de candidature",

  "jobs.title": "Quel type de travail vous intéresse ?",
  "jobs.subtitle": "Cochez tout ce qui vous convient. Vous pourrez changer plus tard.",
  "jobs.empty": "Cette agence n'offre pas encore de types de poste.",
  "jobs.err": "Choisissez au moins un type de poste.",

  "regions.title": "Dans quelles régions pouvez-vous travailler ?",
  "regions.subtitle": "Les shifts durent quelques heures — choisissez des endroits faciles d'accès.",
  "regions.err": "Choisissez au moins une zone.",

  // terms step (T-SA-23.05)
  "terms.title": "Conditions d'utilisation",
  "terms.subtitle": "Lisez et acceptez les conditions avant d'ajouter vos informations personnelles.",
  "terms.err": "Veuillez accepter les Conditions d'utilisation et la Politique de confidentialité pour continuer.",

  "about.title": "Quelques détails sur vous",
  "about.subtitle": "C'est tout ce qu'il nous faut pour étudier votre candidature — aucun document requis pour l'instant.",
  "about.fullName": "Nom complet",
  "about.birthDate": "Date de naissance",
  "about.gender": "Genre",
  "about.gender.pick": "Sélectionnez…",
  "about.gender.male": "Homme",
  "about.gender.female": "Femme",
  "about.gender.preferNotToDisclose": "Je préfère ne pas le préciser",
  "about.taxId": "Numéro d'identification fiscale",
  "about.whatsapp": "Numéro WhatsApp",
  "about.email": "E-mail",
  "about.consent":
    "J'accepte d'être contacté sur WhatsApp pour les invitations, rappels et confirmations de shifts. Nous ne partagerons votre numéro avec personne.",
  "about.consentTelegram": "Je comprends que je devrai m'inscrire via Telegram — je devrai y fournir mon numéro de téléphone (le bouton de partage Telegram), en suivant les instructions de l'étape suivante.",
  "about.docsTitle": "Documents optionnels",
  "about.docsSubtitle":
    "{agency} demande ces documents — les joindre maintenant ne bloque jamais, vous pouvez aussi les ajouter plus tard.",
  "about.attach": "Joindre un document (PDF ou image)",
  "about.attachType": "Joindre : {type} (PDF ou image)",
  "about.remove": "Retirer",
  "about.submit": "Envoyer la candidature",
  "about.submitting": "Envoi de la candidature…",
  "question.yes": "Oui",
  "question.no": "Non",

  "submit.err.selectAgency": "Sélectionnez l'agence à laquelle vous postulez avant d'envoyer.",
  "submit.err.name": "Ajoutez votre nom complet.",
  "submit.err.dob": "Ajoutez votre date de naissance.",
  "submit.err.dobAge": "Vous devez avoir au moins 16 ans pour vous inscrire.",
  "submit.err.dobInvalid": "Vérifiez votre date de naissance.",
  "submit.err.gender": "Sélectionnez votre genre.",
  "submit.err.taxId": "Ajoutez votre numéro d'identification fiscale.",
  "submit.err.whatsapp": "Ajoutez un numéro WhatsApp valide.",
  "submit.err.consent":
    "Acceptez d'être contacté sur WhatsApp — nous ne partagerons votre numéro avec personne.",
  "submit.err.email": "Ajoutez une adresse e-mail valide.",
  "submit.err.flName": "Ajoutez prénom et nom.",
  "submit.err.questions": "Répondez à toutes les questions (ou joignez le document demandé).",
  "submit.err.failed": "Échec de l'envoi. Veuillez réessayer.",
  "submit.err.nameLength": "Ajoutez votre nom complet.",

  "verify.title": "Confirmez votre numéro WhatsApp",
  "verify.body": "Appuyez sur le bouton ci-dessous : WhatsApp s'ouvre avec un message déjà prérempli pour {whatsapp}. Appuyez simplement sur ENVOYER : cela confirme que le numéro est le vôtre (sans code à saisir).",
  "verify.resend": "Je n'ai pas reçu le message - nouveau lien",
  "verify.toast.resent": "Lien renvoyé — ouvrez WhatsApp et appuyez sur ENVOYER.",
  "verify.err.link": "La confirmation WhatsApp n'a pas pu être lancée. Réessayez dans un instant.",
  "verify.whatsapp.openLink": "Ouvrir WhatsApp et appuyer sur ENVOYER",
  "verify.whatsapp.preparing": "Préparation du lien…",
  "verify.whatsapp.waiting": "En attente de ENVOYER… (cette page se met à jour automatiquement)",
  "verify.whatsapp.qrCaption": "Inscription sur ordinateur ? Scannez ce code avec votre téléphone, puis appuyez sur ENVOYER dans WhatsApp.",
  "verify.whatsapp.done": "WhatsApp confirmé — tout est prêt !",
"verify.telegram.title": "Liez votre Telegram",
  "verify.telegram.body": "Appuyez sur le bouton puis START dans le chat Telegram : votre chat sera lié et nous vous enverrons invitations, confirmations et pointages ici.",
  "verify.telegram.openLink": "Ouvrir Telegram et appuyer sur START",
  "verify.telegram.preparing": "Préparation du lien…",
  "verify.telegram.waiting": "En attente de START… (cette page se met à jour automatiquement)",
  "verify.telegram.done": "Telegram lié ✓",
  "verify.telegram.skip": "Continuer sans lier (vous pourrez lier plus tard via « Verify me »)",
  "verify.telegram.toast.linked": "Telegram lié — nous vous écrirons ici.",
  "verify.telegram.err.botConfig": "Telegram n'est pas configuré sur ce site ; choisissez WhatsApp.",
  "verify.switchToWhatsapp": "Je préfère confirmer via WhatsApp →",
  "verify.switchToTelegram": "Je préfère confirmer via Telegram →",
  "verify.waitingNote": "La confirmation peut prendre quelques minutes — patientez. Dès que le message de confirmation arrive en réponse dans votre messagerie, vous pouvez continuer sans problème.",
  "verify.telegram.qrCaption": "Sur mobile ? Scannez ce code pour ouvrir le chat d'inscription dans Telegram, puis appuyez sur START.",
  "verify.telegram.linkCode.title": "Pas de message START prérempli dans le chat ?",
  "verify.telegram.linkCode.body": "Telegram ne préremplit START que pour un tout nouveau chat. Si votre chat existait déjà, ouvrez le bot et envoyez cette commande :",
  "verify.telegram.phoneTitle": "Confirmez votre numéro de téléphone",
  "verify.telegram.phoneBody": "Vous devez fournir le numéro de téléphone pour recevoir les informations d'emploi. Appuyez sur START (ou le bouton de cette page), puis sur le bouton PARTAGER LE NUMÉRO dans le chat : l'inscription ne se termine qu'ensuite.",
  "verify.telegram.phoneBtn": "Confirmer le numéro par Telegram",
  "verify.telegram.phoneToast": "Message envoyé — terminez la confirmation dans Telegram.",

  
  "verify.toast.registered": "Inscription enregistrée. Poursuivez avec les confirmations.",
  "verify.toast.registeredUploadsFailed":
    "Inscription enregistrée, mais {count} document(s) n'ont pas pu être envoyés. Vous pourrez les ajouter plus tard depuis votre profil.",
  
    "verify.err.confirm": "Échec de la confirmation. Veuillez réessayer.",
  "verify.err.send": "Impossible d'envoyer le code WhatsApp. Veuillez réessayer dans un instant.",
  "email.title": "✉️ Vérifiez votre e-mail — nécessaire pour accéder à votre cabinet",
  "email.titleConfirmed": "Votre e-mail est déjà confirmé ✓",
  "email.verified": "Votre e-mail est vérifié. Merci !",
  "email.sent": "Code envoyé à {email}.",
  "email.intro":
    "Vérifiez {email} : votre compte (identifiant) est créé sur cet e-mail dès que le code est confirmé, et le lien de définition du mot de passe y est envoyé — nécessaire pour accéder à votre cabinet.",
  "email.send": "Envoyer le code à mon e-mail",
  "email.verify": "Vérifier l'e-mail",
  "email.resend": "Renvoyer",
  "email.skip": "Passer pour l'instant →",
  "email.toast.sent": "Nous avons envoyé un code à votre e-mail — saisissez-le ci-dessous pour vérifier (optionnel).",
  "email.toast.verified": "E-mail vérifié",
  "email.err.code": "Saisissez le code à 6 chiffres reçu par e-mail.",
  "email.err.send": "Impossible d'envoyer le code à votre e-mail. Veuillez réessayer dans un instant.",
  "email.err.confirm": "Échec de la vérification de l'e-mail. Veuillez réessayer.",
  "email.confirmTitle": "Confirmer votre adresse e-mail",
  "email.focusNote": "Une étape pour terminer : confirmez votre e-mail — la plateforme crée votre compte et vous envoie le lien de définition du mot de passe.",
  "email.whatsappPending": "Votre numéro WhatsApp n'est pas encore confirmé — vous ne serez pas invité(e) aux shifts tant qu'il ne l'est pas.",
  "email.confirmWhatsapp": "Confirmer WhatsApp",
  "email.recommended": "Recommandé — votre compte est créé et le lien de mot de passe est envoyé à cet e-mail dès que le code est confirmé.",
  "header.forgotToast": "Vos données d'inscription ont été effacées de ce navigateur.",
  "header.forgetMyData": "Oublier mes données",
  "header.verifyMeWithName": "Vérifie-toi — {name}",
  "header.registerToLogin": "Inscris-toi et confirme ton compte pour te connecter.",
  "header.loginTooltip": "Connecte-toi si tu as confirmé ton e-mail.",
  "header.confirmEmailToLogin": "Confirme ton e-mail pour te connecter.",
  "header.confirmEmailActionHint": "Cliquez sur « Me vérifier » pour confirmer votre e-mail.",
  "header.emailConfirmedTitle": "Ton e-mail est confirmé.",
  "header.emailConfirmedHint": "Utilise « Déjà inscrit ? Vérifier mon statut » pour te connecter.",
  "header.staleRegistration": "Votre candidature enregistrée n'est plus sur le registre — veuillez vous réinscrire.",
  "header.accountMenu": "Menu du compte",
  "header.registeredWith": "Inscrit chez {agency}",
  "header.forgetConfirmTitle": "Oublier votre inscription enregistrée sur cet appareil ?",
  "header.forgetConfirmBody": "Seule la copie de ce navigateur est supprimée — votre candidature auprès de l'agence n'est pas affectée.",
  "header.forgetConfirm": "Oublier",
  "header.cancel": "Annuler",
  "email.alreadyConfirmed": "Votre e-mail est déjà confirmé ✓",
  "email.passwordSent": "Le lien de définition du mot de passe pour {site} a été envoyé à {email}. Ouvrez-le pour le définir. Si le navigateur affiche un avertissement de sécurité sur ce site, acceptez-le pour continuer.",
  "email.password.resend": "Renvoyer le lien de mot de passe",
  "email.password.toast.resent": "Un nouveau lien de mot de passe a été envoyé à {email}.",
  "email.password.notSent.noSite": "Votre e-mail est confirmé, mais le lien de mot de passe n'a pas pu être envoyé : Contactez l'agence, puis utilisez « Renvoyer le lien de mot de passe ».",
  "email.password.notSent.failed": "Votre e-mail est confirmé, mais le lien de mot de passe n'a pas pu être envoyé. Utilisez « Renvoyer le lien de mot de passe » ou « Mot de passe oublié » sur {site}.",
  "email.password.unknown": "Vous n'avez pas reçu le lien de mot de passe, ou il a expiré ? Utilisez « Renvoyer le lien de mot de passe » ou « Mot de passe oublié » sur {site}.",
  "email.password.err.resend": "Impossible de renvoyer le lien de mot de passe. Veuillez réessayer.",
  "email.password.err.notEligible": "Aucun compte confirmé ne correspond à cet e-mail. Confirmez d'abord votre e-mail.",
  "email.password.err.tooMany": "Un lien de mot de passe vient d'être envoyé. Patientez un instant et réessayez.",
  "email.password.err.passwordSet": "Votre mot de passe est déjà défini. Utilisez « Mot de passe oublié » sur la page de connexion pour le modifier.",
  "header.passwordLink": "Vous n'avez pas reçu le lien de mot de passe ?",
  "email.password.signInPage": "la page de connexion",
  "email.continueToStatus": "Continuer →",
  "email.err.first": "Ajoutez d'abord une adresse e-mail valide.",

  "done.thanks": "Merci, {name} ! Votre profil est en cours de vérification.",
  "done.thanksNoName": "Merci ! Votre profil est en cours de vérification.",
  "done.body":
    "{agency} vérifiera vos informations et vous contactera sur {messenger} une fois votre profil validé. Vous pouvez déjà vous connecter pour suivre votre statut sur le portail complet.",
  "done.messengerAny": "votre messagerie liée",
  "done.cta": "Vérifier mon statut",

  "toast.selected": "Sélectionnée : {name}",
  "toast.whatsappConfirmed": "WhatsApp confirmé",
  "toast.questionsUnavailable":
    "Impossible de charger les questions d'inscription — nous continuons sans elles.",
  "toast.dictLoadFailed": "Impossible de charger le formulaire de candidature.",
  "err.duplicate":
    "Cet e-mail ou cette pièce d'identité est déjà enregistré auprès de {agency}. Utilisez « Déjà inscrit ? Vérifier mon statut » ci-dessus pour vous connecter.",
  "err.http": "Échec de la requête (HTTP {status})",
  "err.registerFailed": "Échec de l'inscription. Veuillez réessayer.",
  "err.uploadFailed": "Échec de l'envoi de « {name} » (HTTP {status}).",
  "err.uploadFailedGeneric": "Échec de l'envoi du document.",
    "err.codeIncorrect.email": "Code incorrect. Vérifiez votre boîte de réception et réessayez.",
  "err.retry": "Réessayer",
  "demo.emailCode": "[démo] Code de vérification e-mail : {code}",
  "lang.tip": "Utilisez le sélecteur de langue pour passer à {language}",
  "demo.agencyName": "Agence de démonstration",
  "header.noAgency": "Aucune agence disponible",
  "landing.noAgency": "Aucune agence disponible pour le moment — aucun emploi temporaire n'est annoncé pour l'instant. Veuillez réessayer plus tard.",
  "landing.genericBlurb": "Nous avons des emplois temporaires dans les événements, l'hôtellerie, les entrepôts et plus — inscrivez-vous une fois et recevez des invitations aux shifts qui vous correspondent, directement sur WhatsApp.",
  "landing.preselectBlurb": "Sélectionnez l'agence à laquelle vous souhaitez postuler — son offre apparaîtra ici.",
  "questions.docFallback": "document d'inscription",
};

const dictionaries: Record<Locale, Partial<Record<TKey, string>>> = { en, es, fr };

/** Locale detection (modern approach): `navigator.languages` / regional settings → base tag match → English. */
export function detectLocale(): Locale {
  const base = browserLocaleBase();
  return base ?? "en";
}

/** The BASE language tag of the user's browser/regional preference (e.g. "es" for es-MX) —
 * null when the browser does not expose one or the preference is not a supported locale. */
export function browserLocaleBase(): Locale | null {
  const candidates: string[] = Array.isArray(navigator.languages) && navigator.languages.length
    ? navigator.languages
    : [navigator.language || ""];
  for (const tag of candidates) {
    const candidate = (tag || "").toLowerCase().split(/[-_]/)[0];
    if (candidate.length >= 2 && (SUPPORTED_LOCALES as readonly string[]).includes(candidate)) {
      return candidate as Locale;
    }
  }
  return null;
}

/** PURE resolution priority (the PRD §16.1 language rules): the user's stored pick wins over
 * the browser preference; an unsupported/absent stored value is ignored; English is the fallback. */
export function resolveLocale(browserBase: Locale | null, storedLanguage: string | null): Locale {
  const stored = (storedLanguage ?? "").toLowerCase();
  if (stored.length === 2 && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
    return stored as Locale;
  }
  return browserBase ?? "en";
}

const LS_LANGUAGE = "staffing.language";

function storedLocale(): Locale | null {
  const value = (localStorage.getItem(LS_LANGUAGE) ?? "").toLowerCase();
  return (SUPPORTED_LOCALES as readonly string[]).includes(value) ? (value as Locale) : null;
}

export const LANGUAGE_NAMES: Record<Locale, string> = { en: "English", es: "Español", fr: "Français" };

/**
 * country.* dictionary (2026-10-02): country names BY ISO CODE, translated per locale —
 * used for the "Operates in <country>" mention and the agency cards, so switching the
 * language also translates the country. Covers the rollout countries (PRD: Spain and
 * France at launch; ~7 by mid-2027); MISSING codes fall back to the server-provided
 * registry name (never an untranslated guess).
 */
const countryNames: Record<string, Partial<Record<Locale, string>>> = {
  AD: { en: "Andorra", es: "Andorra", fr: "Andorre" },
  AT: { en: "Austria", es: "Austria", fr: "Autriche" },
  BE: { en: "Belgium", es: "Bélgica", fr: "Belgique" },
  CH: { en: "Switzerland", es: "Suiza", fr: "Suisse" },
  DE: { en: "Germany", es: "Alemania", fr: "Allemagne" },
  ES: { en: "Spain", es: "España", fr: "Espagne" },
  FR: { en: "France", es: "Francia", fr: "France" },
  IT: { en: "Italy", es: "Italia", fr: "Italie" },
  MD: { en: "Moldova", es: "Moldavia", fr: "Moldavie" },
  PT: { en: "Portugal", es: "Portugal", fr: "Portugal" },
  RO: { en: "Romania", es: "Rumanía", fr: "Roumanie" },
};

/**
 * The country AS IT FITS INTO A PHRASE (grammar-aware): for FRENCH it applies the "de/d'"
 * elision for vowel/h-initial names ("d'Espagne" vs "de Suisse" — the FR heading template
 * therefore contains NO own "de"); any other locale returns just the translated name
 * (EN/ES templates carry their own "of"/"de", which need no elision).
 */
export function countryInPhrase(
  code: string | null | undefined,
  fallbackName?: string | null,
  target?: Locale,
): string {
  const name = countryLabel(code, fallbackName, target);
  const locale = target ?? getLocale();
  if (locale === "fr" && /^[aeiouàâäéèêëîïôöùûüœh]/i.test(name)) {
    return `d'${name}`;
  }
  if (locale === "fr") {
    return `de ${name}`;
  }
  return name;
}

/** The translated country display name for `code` in the active (or explicit) locale;
 * falls back to the registry name the server provided, else to the code itself. */
export function countryLabel(
  code: string | null | undefined,
  fallbackName?: string | null,
  target?: Locale,
): string {
  const normalized = (code ?? "").toUpperCase();
  if (!normalized) return "";
  const translated = countryNames[normalized]?.[target ?? getLocale()];
  return translated ?? fallbackName ?? normalized;
}

/** The active locale: the USER'S SELECTION from local storage when present,
 * otherwise the browser preference (fallback English). */
let current: Locale = resolveLocale(browserLocaleBase(), localStorage.getItem(LS_LANGUAGE));
if (typeof document !== "undefined") {
  document.documentElement.lang = current;
}

const listeners = new Set<() => void>();

/** Currently rendered locale. */
export function getLocale(): Locale {
  return current;
}

/** The language stored by the language switcher, if the user ever picked one. */
export function hasStoredLocale(): boolean {
  return storedLocale() !== null;
}

/** Persist the user's language choice and re-render the site in it. */
export function setLocale(target: Locale): void {
  localStorage.setItem(LS_LANGUAGE, target);
  if (target === current) return;
  current = target;
  if (typeof document !== "undefined") {
    document.documentElement.lang = current;
  }
  listeners.forEach((notify) => notify());
}

/** React binding — components calling it re-render on a language switch. */
export function useLocale(): Locale {
  const [, bump] = useState(0);
  useEffect(() => {
    const notify = () => bump((n) => n + 1);
    listeners.add(notify);
    return () => {
      listeners.delete(notify);
    };
  }, []);
  return current;
}

/**
 * Translate in an EXPLICIT locale — used for the browser-preference tip under the
 * language switcher, which must always speak the browser's language.
 */
export function tFor(
  target: Locale,
  key: TKey,
  params?: Record<string, string | undefined>,
): string {
  const translated = dictionaries[target][key] ?? en[key];
  if (!params) return translated;
  let result = translated;
  for (const [name, value] of Object.entries(params)) {
    result = result.split(`{${name}}`).join(value ?? "");
  }
  return result;
}

/**
 * Translate a key in the active locale, falling back to English — e.g.
 * `t("verify.body", { whatsapp: "+34600…" })`; `{placeholders}` in the dictionary string
 * are replaced with the params.
 */
export function t(key: TKey, params?: Record<string, string | undefined>): string {
  return tFor(current, key, params);
}